import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { queryClient } from "@/config/queryClient";
import { message } from "antd";
import {
  TimesheetEntry,
  TimesheetOfWeek,
  EmployeeTimesheetData,
} from "../types/Timesheet";
import { USER_TIMESHEET } from "../config/api";

const getTimesheetByUserId = async (
  userId: number,
  timestamp: string
): Promise<AxiosResponse<TimesheetOfWeek>> =>
  axios.get(`/api/timesheet/${userId}`, {
    params: {
      date: timestamp,
    },
  });

export const useGetTimesheetByUserId = (userId: number, timestamp: string) => {
  return useQuery({
    queryFn: async () => getTimesheetByUserId(userId, timestamp),
    queryKey: [USER_TIMESHEET, "GET /", userId, timestamp],
    select: (axiosResponse) => {
      return axiosResponse.data;
    },
  });
};

const updateTimesheet = async (
  date: string,
  userId: number,
  entries: TimesheetEntry[],
  deleteEntries: number[]
) => {
  return axios.post(`/api/timesheet/${userId}`, {
    date,
    entries,
    deleteEntries,
  });
};

export const useSaveTimesheet = (userId: number) => {
  return useMutation({
    mutationFn: ({
      date,
      entries,
      deleteEntries,
    }: {
      date: string;
      entries: TimesheetEntry[];
      deleteEntries: number[];
    }) => updateTimesheet(date, userId, entries, deleteEntries),
    onSuccess: () => {
      queryClient.invalidateQueries([USER_TIMESHEET, "GET /", userId]);
      message.success("Timesheet saved successfully");
    },
  });
};

const changeWeekStatus = async (
  userId: number,
  timesheetId: number,
  status: "APPROVED" | "PENDING"
) => {
  return axios.patch(`/api/timesheet/${userId}`, {
    timesheetId,
    status,
  });
};

export const useChangeTimesheetWeekStatus = (userId: number) => {
  return useMutation({
    mutationFn: ({
      timesheetId,
      status,
    }: {
      timesheetId: number;
      status: "APPROVED" | "PENDING";
    }) => changeWeekStatus(userId, timesheetId, status),
    onSuccess: () => {
      queryClient.invalidateQueries([USER_TIMESHEET, "GET /", userId]);
      message.success("Timesheet status updated successfully");
    },
  });
};

export const exportTimesheet = async ({
  userIds,
  from,
  to,
}: {
  userIds: number[];
  from: string;
  to: string;
}): Promise<
  AxiosResponse<{
    data: {
      from: string;
      to: string;
      result: EmployeeTimesheetData[];
    };
  }>
> => {
  return axios.get("/api/timesheet", {
    params: {
      users: userIds,
      from,
      to,
    },
  });
};
